import React from "react";
import { Building2, ExternalLink, User } from "lucide-react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { Button, ButtonLink } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { FieldLabel, TextInput } from "../../../components/ui/FormControls";
import type { MainAppPageProps } from "../types";

type AccountPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "userDisplay"
> & {
  canAccessBackend?: boolean;
  hasBusiness?: boolean;
};

export const AccountPage: React.FC<AccountPageProps> = ({
  csrfToken,
  routes,
  userDisplay,
  canAccessBackend = false,
  hasBusiness = false,
}) => {
  const avatarUrl = userDisplay?.avatarUrl ?? null;
  const logoUrl = userDisplay?.logoUrl ?? null;
  const hasCustomPhoto = userDisplay?.hasCustomPhoto ?? false;
  const hasLogo = userDisplay?.hasLogo ?? false;
  const showWebsitePhotos = canAccessBackend && hasBusiness;

  return (
    <DashboardPageLayout title="Account">
      <div className="flex w-full max-w-md flex-col gap-10">
        <form
          action={routes.account}
          method="post"
          encType="multipart/form-data"
          className="flex flex-col gap-10"
        >
          <AuthenticityTokenField csrfToken={csrfToken} />
          <input type="hidden" name="_method" value="patch" />

          <Card className="bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Name</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <FieldLabel htmlFor="user_name">Your name</FieldLabel>
                <TextInput
                  id="user_name"
                  name="user[name]"
                  defaultValue={userDisplay?.userName ?? ""}
                  maxLength={100}
                  required
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Profile photo</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-start gap-6">
              <div className="flex items-center gap-6">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="h-24 w-24 rounded-full border border-border object-cover"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-full border border-border bg-muted text-muted-foreground">
                    <User className="h-12 w-12" />
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <div>
                    <FieldLabel htmlFor="user_avatar">
                      Upload a photo
                    </FieldLabel>
                    <input
                      id="user_avatar"
                      name="avatar"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic"
                      className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-foreground"
                    />
                  </div>
                  {showWebsitePhotos ? (
                    <ButtonLink
                      variant="outline"
                      size="sm"
                      href={`${routes.dashboard}?photoFocus=profile`}
                      aria-label="Choose profile photo from dashboard library"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Set from photo library
                    </ButtonLink>
                  ) : null}
                </div>
              </div>
              {hasCustomPhoto ? (
                <p className="text-sm text-muted-foreground">
                  {showWebsitePhotos
                    ? "Shown on your public site."
                    : "Shown to your team in Toolkit."}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Button type="submit" variant="premium">
            Save account
          </Button>
        </form>

        {showWebsitePhotos ? (
          <Card className="bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Business logo</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-start gap-6">
              <div className="flex items-center gap-6">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Business logo"
                    className="h-24 w-24 rounded-lg border border-border object-contain p-1"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
                    <Building2 className="h-12 w-12" />
                  </div>
                )}
                <ButtonLink
                  variant="outline"
                  size="sm"
                  href={`${routes.dashboard}?photoFocus=logo`}
                  aria-label="Choose business logo from dashboard library"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Set from photo library
                </ButtonLink>
              </div>
              {hasLogo ? (
                <p className="text-sm text-muted-foreground">
                  Header, footer, and browser tab.
                </p>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </DashboardPageLayout>
  );
};
